import { z } from 'zod'

import {
  buildDesktopServiceOAuthInstallUrl,
  type DesktopServiceOAuthProvider,
  type DesktopServiceOAuthRequest,
} from '@trevize/desktop-oauth-contract'

export const desktopAuthorizationSearchSchema = z.object({
  client_id: z.string(),
  redirect_uri: z.string(),
  state: z.string(),
  code_challenge: z.string(),
  code_challenge_method: z.literal('S256'),
  // Previously released desktop hosts do not include a provider.
  provider: z.enum(['github', 'linear']).optional(),
  oauth_complete: z.union([z.literal(true), z.literal('true')]).optional(),
})

export type DesktopAuthProvider = 'github' | 'linear'
export type { DesktopServiceOAuthProvider, DesktopServiceOAuthRequest }

export type DesktopBridge = {
  auth: {
    signIn: (provider: DesktopAuthProvider) => Promise<void>
    cancel: () => Promise<void>
  }
  serviceOAuth?: (request: DesktopServiceOAuthRequest) => Promise<boolean>
  refreshHostedClient?: () => Promise<boolean>
  retryHostedClient?: () => Promise<boolean>
}

export function buildDesktopOAuthContinuationUrl(input: {
  origin: string
  provider: DesktopAuthProvider
  clientId: string
  redirectUri: string
  state: string
  codeChallenge: string
}): string {
  const url = new URL('/desktop/authorize', input.origin)
  url.searchParams.set('client_id', input.clientId)
  url.searchParams.set('redirect_uri', input.redirectUri)
  url.searchParams.set('state', input.state)
  url.searchParams.set('code_challenge', input.codeChallenge)
  url.searchParams.set('code_challenge_method', 'S256')
  url.searchParams.set('provider', input.provider)
  url.searchParams.set('oauth_complete', 'true')
  return url.toString()
}

export function desktopBridge(): DesktopBridge | null {
  return typeof window !== 'undefined' && window.trevize ? window.trevize : null
}

/** Launch a workspace integration OAuth flow in the system browser on desktop.
 * A regular browser keeps the existing hosted navigation behavior. */
export async function launchServiceOAuth(input: {
  provider: DesktopServiceOAuthProvider
  workspaceId: string
  apiOrigin: string
}): Promise<void> {
  const bridge = desktopBridge()
  if (bridge?.serviceOAuth) {
    const opened = await bridge.serviceOAuth({
      provider: input.provider,
      workspaceId: input.workspaceId,
    })
    if (!opened) throw new Error('Could not open the system browser')
    return
  }
  const url = buildServiceOAuthUrl(input)
  if (input.provider === 'github') {
    window.location.assign(url)
  } else {
    window.open(url, `${input.provider}-install`, 'width=600,height=700')
  }
}

export function buildServiceOAuthUrl(input: {
  provider: DesktopServiceOAuthProvider
  workspaceId: string
  apiOrigin: string
}): string {
  return buildDesktopServiceOAuthInstallUrl(input.apiOrigin, {
    provider: input.provider,
    workspaceId: input.workspaceId,
  })
}

/** Keep post-login navigation on the current application origin. */
export function safeLocalRedirect(
  value: string | undefined,
  origin: string,
): string {
  if (!value || !value.startsWith('/') || value.startsWith('//')) return '/'
  try {
    const url = new URL(value, origin)
    return url.origin === new URL(origin).origin
      ? `${url.pathname}${url.search}${url.hash}`
      : '/'
  } catch {
    return '/'
  }
}
